import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, Bell, ChevronRight, CirclePause, ClipboardList, FileChartColumn, HandCoins, Languages, LogOut, PackageSearch, Repeat, Users, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { logout, clearShopSelection } from '@/features/auth/authSlice';
import { clearCart } from '@/features/cart/cartSlice';
import { Avatar, Badge, Card, Overline } from '@/components/ui';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';
import { APP_VERSION } from '@/utils/constants';
import { hasPendingSales } from '@/features/checkout/checkoutQueue';
import { useUnreadNotifications } from '@/features/notifications/useUnreadNotifications';

/**
 * 16 Lainnya — profile card, module shortcuts, preferences, logout.
 * Landscape: 400px menu column + preferences detail. Rows navigate to flat routes.
 */
const SettingsPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user, selectedShop } = useAppSelector((s) => s.auth);
    const heldCartCount = useAppSelector((s) => s.cart.heldCartCount) || 0;
    const unread = useUnreadNotifications();

    const handleLogout = async () => {
        if (hasPendingSales()) {
            toast.error(t('queue.logoutBlocked'));
            return;
        }
        await dispatch(logout());
        navigate('/login');
    };

    const handleSwitchShop = () => {
        dispatch(clearCart());
        dispatch(clearShopSelection());
        navigate('/shop-select');
    };

    const links = [
        // The landscape rail has its own Ditahan item
        { to: '/held-carts', icon: CirclePause, label: t('heldCarts.title'), badge: heldCartCount ? <Badge tone="accent">{heldCartCount}</Badge> : null, phoneOnly: true },
        { to: '/reports/daily', icon: FileChartColumn, label: t('report.title'), badge: null },
        { to: '/topup', icon: Wallet, label: t('topup.title'), badge: null },
        { to: '/notifications', icon: Bell, label: t('notifications.title'), badge: unread ? <Badge tone="danger">{unread}</Badge> : null },
        { to: '/attendance', icon: Users, label: t('settings.staffAttendance'), badge: null },
    { to: '/petty-cash', icon: HandCoins, label: t('settings.pettyCash'), badge: null },
        { to: '/reimbursement', icon: ArrowLeftRight, label: t('settings.reimbursement'), badge: null },
        { to: '/stock-request', icon: PackageSearch, label: t('settings.stockRequest'), badge: null },
        { to: '/stock-opname', icon: ClipboardList, label: t('settings.stockOpname'), badge: null },
    ];

    const preferences = (
        <Card padding="none">
            <div className="flex items-center gap-3 px-4 py-3.5">
                <Languages className="h-[19px] w-[19px] text-ink-brand" />
                <span className="flex-1 text-sm font-bold">{t('common.language')}</span>
                <LanguageSwitcher />
            </div>
        </Card>
    );

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col lg:flex-row">
            <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-5 pb-6 pt-2.5 lg:w-[400px] lg:flex-none lg:border-r lg:border-line-subtle lg:px-5 lg:pt-[22px]">
                <h1 className="text-2xl font-black lg:text-[26px]">{t('nav.more')}</h1>

                <div className="flex items-center gap-3 rounded-xl bg-surface-brand p-4 text-white shadow-brand">
                    <Avatar name={user?.fullname} size="lg" ring />
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-base font-black">{user?.fullname}</p>
                        <p className="truncate text-xs text-primary-100">{selectedShop?.shopName} · {selectedShop?.role}</p>
                    </div>
                    <button type="button" onClick={handleSwitchShop} className="flex h-9 shrink-0 items-center gap-1.5 rounded-pill bg-white/[.14] px-3 text-[13px] font-extrabold">
                        <Repeat className="h-[15px] w-[15px]" />
                        {t('settings.switchShop')}
                    </button>
                </div>

                <Card padding="none">
                    {links.map((l) => (
                        <button key={l.to} type="button" onClick={() => navigate(l.to)} className={`flex w-full items-center gap-3 border-b border-line-subtle px-4 py-[13px] text-left last:border-b-0 hover:bg-surface-sunken ${'phoneOnly' in l && l.phoneOnly ? 'lg:hidden' : ''}`}>
                            <l.icon className="h-[19px] w-[19px] text-ink-brand" />
                            <span className="flex-1 text-[15px] font-bold">{l.label}</span>
                            {l.badge ?? <ChevronRight className="h-[17px] w-[17px] text-ink-muted" />}
                        </button>
                    ))}
                </Card>

                <div className="lg:hidden">
                    <Overline className="px-0.5 pb-2 pt-1">{t('settings.preferences')}</Overline>
                    {preferences}
                </div>

                <div className="flex-1" />
                <button type="button" onClick={handleLogout} className="flex items-center gap-2.5 rounded-md px-3.5 py-[13px] text-[15px] font-extrabold text-status-danger hover:bg-status-danger-soft">
                    <LogOut className="h-[19px] w-[19px]" />
                    {t('auth.logout')}
                </button>
                <p className="text-center text-xs text-ink-muted">v{APP_VERSION}</p>
            </div>

            <div className="hidden min-w-0 flex-1 flex-col gap-3 p-[22px_28px] lg:flex">
                <h2 className="text-[22px] font-black">{t('settings.preferences')}</h2>
                {preferences}
            </div>
        </div>
    );
};

export default SettingsPage;
